import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { quotesAPI, adminAPI, contractsAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  ArrowLeft, Pencil, Copy, Trash2, FileDown, Send, CheckCircle2,
  AlertCircle, Server, Database, Package, User, FileText, Clock, Globe, Users, Loader2, PenLine, Shield, Wand2, ClipboardCopy, PackageCheck, Building2, FileSignature, GitBranch, Star
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const NAVY = 'hsl(var(--c4p-navy-900))';
const ORANGE = 'hsl(var(--c4p-orange-600))';

const STATUS_STYLES = {
  DRAFT:    { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
  SENT:     { bg: '#f0f9ff', color: '#0c4a6e', border: '#bae6fd' },
  ACCEPTED: { bg: '#f0fdf4', color: '#14532d', border: '#bbf7d0' },
  EXPIRED:  { bg: '#fffbeb', color: '#78350f', border: '#fde68a' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.DRAFT;
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{status}</span>;
};

const sym = (code) => ({ GBP: '\u00a3', EUR: '\u20ac', USD: '$', CAD: 'CA$', AUD: 'A$' }[code] || code);

const STATUS_ACCENT = { DRAFT: '#94a3b8', SENT: '#0ea5e9', ACCEPTED: '#16a34a', EXPIRED: '#f59e0b' };

function SectionCard({ icon: Icon, iconBg, iconColor, title, value, editStep, quoteId, isOwner }) {
  return (
    <div className="rounded-xl border bg-white p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: iconBg }}>
          <Icon size={15} style={{ color: iconColor }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: 'hsl(var(--muted-fg))' }}>{title}</p>
            {editStep && isOwner && (
              <Link to={`/quotes/${quoteId}/edit?step=${editStep}`}>
                <Button variant="ghost" size="sm" className="gap-1 text-xs flex-shrink-0 -mt-1 -mr-2">
                  <Pencil size={11} /> Edit
                </Button>
              </Link>
            )}
          </div>
          <div>{value}</div>
        </div>
      </div>
    </div>
  );
}

// ── Variant Comparison Modal ──────────────────────────────────────────────────
// Displays a full side-by-side breakdown of all variants in the same group.
// Rows that differ across variants are highlighted in amber.
function VariantCompareModal({ variants, currentId, onClose }) {
  const _NAVY = 'hsl(var(--c4p-navy-900))';
  const _SYM = { GBP: '£', EUR: '€', USD: '$', CAD: 'CA$', AUD: 'A$' };
  const sy = (code) => _SYM[code] || '';
  const fmtN = (n) => Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtM = (n, code) => n != null ? `${sy(code)}${fmtN(n)}` : '—';

  const STATUS_STYLE = {
    DRAFT:    { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
    SENT:     { bg: '#f0f9ff', color: '#0c4a6e', border: '#bae6fd' },
    ACCEPTED: { bg: '#f0fdf4', color: '#14532d', border: '#bbf7d0' },
    EXPIRED:  { bg: '#fffbeb', color: '#78350f', border: '#fde68a' },
  };

  const PRODUCT_LABELS = { STANDARD: 'C4P Standard', ADVANCED: 'C4P Advanced', MTPRO: 'C4P MTPro' };

  // Tenant Direct: show the grossed-up tenant invoice, not the base/partner total.
  const displayTotal = (v) => {
    const snap = v.pricing_snapshot;
    if (v.billing_model === 'TENANT_DIRECT' && snap?.tenant_invoice_monthly) return snap.tenant_invoice_monthly;
    return snap?.monthly_total ?? null;
  };

  const sections = [
    {
      title: 'Pricing',
      rows: [
        { label: 'Monthly Total',  key: 'monthly_total',  getValue: v => v.pricing_snapshot ? fmtM(displayTotal(v), v.currency) : '—', numericKey: v => displayTotal(v) },
        { label: 'Term Total',     key: 'term_total',     getValue: v => v.pricing_snapshot ? fmtM(v.pricing_snapshot.term_total, v.currency) : '—' },
        { label: 'PUPM',           key: 'pupm',           getValue: v => v.pricing_snapshot?.pupm_price != null ? fmtM(v.pricing_snapshot.pupm_price, v.currency) : '—' },
        { label: 'Currency',       key: 'currency',       getValue: v => v.currency || '—' },
        { label: 'Billing Freq.',  key: 'billing_freq',   getValue: v => v.billing_frequency || '—' },
      ],
    },
    {
      title: 'Users & Term',
      rows: [
        { label: 'Named Users',      key: 'users',    getValue: v => v.num_users ?? '—' },
        { label: 'Web Client Users', key: 'wc_users', getValue: v => v.num_web_client_users || 0 },
        { label: 'Mobile Users',     key: 'mob',      getValue: v => v.num_mobile_users || 0 },
        { label: 'Term (months)',    key: 'term',     getValue: v => v.term_months ?? '—' },
        { label: 'User Tier',        key: 'tier',     getValue: v => v.pricing_snapshot?.user_tier_label || '—' },
      ],
    },
    {
      title: 'Infrastructure',
      rows: [
        { label: 'Product',          key: 'product',     getValue: v => { const lbl = PRODUCT_LABELS[v.product_code]; return lbl || (v.quote_category === 'MANUAL' ? 'C4P Custom (Manual)' : v.quote_category === 'TEST_SYSTEM' ? 'C4P Custom Test' : v.product_code || '—'); } },
        { label: 'Region',           key: 'region',      getValue: v => v.region_name || '—' },
        { label: 'DB Type',          key: 'db_type',     getValue: v => v.db_server?.db_type || v.db_type || '—' },
        { label: 'DB Server',        key: 'db_svr',      getValue: v => v.db_server?.server_description || '—' },
        { label: 'DB Extra Storage', key: 'db_storage',  getValue: v => v.db_server?.additional_data_gb ? `+${v.db_server.additional_data_gb}GB` : '—' },
        { label: 'App Servers',      key: 'app_svrs',    getValue: v => v.app_servers?.filter(s => !s.is_included_in_pupm).length || 0 },
        { label: 'File Storage',     key: 'file_gb',     getValue: v => v.file_storage_gb ? `${v.file_storage_gb}GB` : '—' },
        { label: 'Extras',           key: 'extras',      getValue: v => v.extras?.length ? v.extras.map(e => e.extra_name).join(', ') : '—' },
      ],
    },
    {
      title: 'Quote Details',
      rows: [
        { label: 'Status',       key: 'status',    getValue: v => v.status || '—' },
        { label: 'Expiry Date',  key: 'expiry',    getValue: v => v.expiry_date || 'None' },
        { label: 'Required By',  key: 'req_by',    getValue: v => v.required_by_date || (v.use_next_available_slot ? 'Next slot' : '—') },
        { label: 'PSM',          key: 'psm',       getValue: v => v.psm_user_name || '—' },
        { label: 'Payment',      key: 'payment',   getValue: v => v.payment_method || '—' },
      ],
    },
  ];

  const isDiff = (row) => {
    const vals = variants.map(v => String(row.getValue(v)));
    return new Set(vals).size > 1;
  };

  // For monthly total highlight: find min/max (Tenant Direct uses the grossed-up tenant invoice)
  const totals = variants.map(v => displayTotal(v)).filter(t => t !== null);
  const minTotal = totals.length ? Math.min(...totals) : null;
  const maxTotal = totals.length ? Math.max(...totals) : null;

  // ── Export helpers (Copy Table / Download CSV) ────────────────────────────
  const columnHeader = (v) => {
    const isCurrent = v.id === currentId;
    return [v.quote_number, v.variant_label, isCurrent ? 'current' : null,
            v.is_primary_deal_quote ? 'Primary' : null, v.status].filter(Boolean).join(' · ');
  };

  // Tenant Direct: use the saved bespoke tenant price if set, else the margin formula —
  // mirrors QuoteDetailPage so a quote's numbers read identically everywhere.
  const displayLineAmount = (v, li) => {
    if (v.billing_model !== 'TENANT_DIRECT') return li.monthly_amount;
    if (li.tenant_unit_price != null) return li.tenant_monthly_amount ?? li.monthly_amount;
    const snap = v.pricing_snapshot || {};
    if (li.category === 'base') {
      const tenantUnit = (li.unit_price || 0) + (snap.partner_margin_pupm || 0);
      return Math.round(tenantUnit * (snap.num_users || 0) * 100) / 100;
    }
    if ((li.monthly_amount || 0) > 0) {
      const factor = 1 + ((snap.partner_margin_percent || 0) / 100);
      return Math.round(li.monthly_amount * factor * 100) / 100;
    }
    return li.monthly_amount;
  };

  const getLineItemRows = () => {
    const labelSet = new Set();
    const allLabels = [];
    variants.forEach(v => {
      (v.pricing_snapshot?.line_items || [])
        .filter(li => !li.is_included && (li.monthly_amount || 0) > 0)
        .forEach(li => {
          const key = `${li.category}::${li.label}`;
          if (!labelSet.has(key)) { labelSet.add(key); allLabels.push({ key, label: li.label }); }
        });
    });
    return allLabels.map(({ key, label }) => ({
      label,
      values: variants.map(v => {
        const li = (v.pricing_snapshot?.line_items || []).find(l => `${l.category}::${l.label}` === key);
        return li ? fmtM(displayLineAmount(v, li), v.currency) : '—';
      }),
    }));
  };

  const buildExportRows = () => {
    const rows = [];
    sections.forEach(section => {
      rows.push({ section: section.title });
      section.rows.forEach(row => rows.push({ label: row.label, values: variants.map(v => String(row.getValue(v))) }));
    });
    const lineItemRows = getLineItemRows();
    if (lineItemRows.length) {
      rows.push({ section: 'Pricing Breakdown' });
      lineItemRows.forEach(r => rows.push(r));
    }
    return rows;
  };

  const escapeHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const handleCopyTable = async () => {
    const rows = buildExportRows();
    const headerCols = ['Field', ...variants.map(columnHeader)];

    let html = '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px;">';
    html += '<tr>' + headerCols.map(h => `<th style="background:#f1f5f9;text-align:left;padding:6px 10px;">${escapeHtml(h)}</th>`).join('') + '</tr>';
    rows.forEach(r => {
      if (r.section) {
        html += `<tr><td colspan="${headerCols.length}" style="background:#eff6ff;font-weight:bold;color:#1e40af;padding:6px 10px;">${escapeHtml(r.section.toUpperCase())}</td></tr>`;
      } else {
        html += '<tr>' + `<td style="padding:6px 10px;font-weight:600;">${escapeHtml(r.label)}</td>` +
          r.values.map(v => `<td style="padding:6px 10px;">${escapeHtml(v)}</td>`).join('') + '</tr>';
      }
    });
    html += '</table>';

    let text = headerCols.join('\t') + '\n';
    rows.forEach(r => {
      text += r.section ? r.section.toUpperCase() + '\n' : [r.label, ...r.values].join('\t') + '\n';
    });

    try {
      if (window.ClipboardItem) {
        const item = new window.ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(text);
      }
      toast.success('Comparison table copied — paste into an email or doc');
    } catch {
      try { await navigator.clipboard.writeText(text); toast.success('Comparison table copied as text'); }
      catch { toast.error('Failed to copy table'); }
    }
  };

  const handleDownloadPDF = async () => {
    const tid = toast.loading('Generating comparison PDF…');
    try {
      const token = localStorage.getItem('c4p_access_token');
      const res = await fetch(`${API_BASE}/quotes/${currentId}/variant-comparison-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `variant-comparison-${variants[0]?.quote_number || 'quotes'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.dismiss(tid);
      toast.success('Comparison PDF downloaded');
    } catch (e) { toast.dismiss(tid); toast.error(e.message); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Modal header */}
        <div className="px-6 pt-5 pb-4 flex items-center justify-between gap-3 flex-shrink-0"
          style={{ borderBottom: '1px solid #e2e8f0', background: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: '#eff6ff' }}>
              <GitBranch size={14} style={{ color: '#1e40af' }} />
            </div>
            <DialogHeader className="text-left p-0">
              <DialogTitle style={{ fontFamily: 'var(--font-display)', color: _NAVY, fontSize: '1rem' }}>
                Variant Comparison
              </DialogTitle>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                {variants.length} variants · amber rows differ across variants
              </p>
            </DialogHeader>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="flex-shrink-0">Close</Button>
        </div>

        <div className="px-6 pt-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopyTable} className="gap-1.5" data-testid="variant-compare-copy-button">
            <ClipboardCopy size={13} /> Copy Table
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} className="gap-1.5" data-testid="variant-compare-download-button">
            <FileDown size={13} /> Download PDF
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            {/* Sticky column headers — one per variant */}
            <thead>
              <tr>
                {/* Row label column */}
                <th style={{ width: '140px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0',
                  padding: '10px 16px', textAlign: 'left', position: 'sticky', left: 0, zIndex: 2, fontSize: '11px',
                  color: 'hsl(var(--muted-fg))', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Field
                </th>
                {variants.map((v) => {
                  const isCurrent = v.id === currentId;
                  const ss = STATUS_STYLE[v.status] || STATUS_STYLE.DRAFT;
                  return (
                    <th key={v.id}
                      style={{
                        minWidth: '180px', padding: '10px 16px', textAlign: 'left', verticalAlign: 'top',
                        borderBottom: '2px solid #e2e8f0',
                        background: isCurrent ? '#eff6ff' : '#f8fafc',
                        borderTop: isCurrent ? '2px solid #1e40af' : '2px solid transparent',
                      }}>
                      <div className="space-y-1">
                        {/* Variant label */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded"
                            style={{ background: isCurrent ? '#dbeafe' : '#e2e8f0', color: isCurrent ? '#1e40af' : '#475569',
                                     fontFamily: 'var(--font-mono, monospace)' }}>
                            {v.variant_label}
                          </span>
                          {isCurrent && (
                            <span className="text-xs px-1.5 py-0.5 rounded"
                              style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}>
                              current
                            </span>
                          )}
                          {v.is_primary_deal_quote && (
                            <span className="text-xs px-1 py-0.5 rounded flex items-center gap-0.5"
                              style={{ background: '#fefce8', color: '#854d0e' }}>
                              <Star size={8} fill="currentColor" /> Primary
                            </span>
                          )}
                        </div>
                        {/* Quote number link */}
                        {v.id !== currentId ? (
                          <a href={`/quotes/${v.id}`} className="text-xs font-mono hover:underline block"
                            style={{ color: 'hsl(var(--muted-fg))' }} target="_blank" rel="noopener noreferrer">
                            {v.quote_number} ↗
                          </a>
                        ) : (
                          <span className="text-xs font-mono block" style={{ color: 'hsl(var(--muted-fg))' }}>
                            {v.quote_number}
                          </span>
                        )}
                        {/* Status pill */}
                        <span className="text-xs px-1.5 py-0.5 rounded-full inline-block"
                          style={{ background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}>
                          {v.status}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {sections.map((section, si) => (
                <React.Fragment key={section.title}>
                  {/* Section heading row */}
                  <tr>
                    <td colSpan={variants.length + 1}
                      style={{ padding: '8px 16px 4px', background: '#f8fafc',
                               borderBottom: '1px solid #e2e8f0', borderTop: si > 0 ? '2px solid #e2e8f0' : 'none' }}>
                      <span className="text-xs font-bold uppercase tracking-widest"
                        style={{ color: '#1e40af' }}>
                        {section.title}
                      </span>
                    </td>
                  </tr>

                  {section.rows.map((row, ri) => {
                    const diff = isDiff(row);
                    const values = variants.map(v => row.getValue(v));
                    return (
                      <tr key={row.key}
                        style={{
                          background: diff ? 'rgb(251 191 36 / 0.07)' : (ri % 2 === 0 ? 'white' : '#f8fafc'),
                          borderBottom: '1px solid #f1f5f9',
                        }}>
                        {/* Row label */}
                        <td style={{ padding: '9px 16px', fontWeight: 600, fontSize: '12px',
                          color: 'hsl(var(--muted-fg))', position: 'sticky', left: 0, zIndex: 1,
                          background: diff ? 'rgb(251 191 36 / 0.07)' : (ri % 2 === 0 ? 'white' : '#f8fafc'),
                          whiteSpace: 'nowrap' }}>
                          {diff && <span className="mr-1" style={{ color: '#d97706' }}>◈</span>}
                          {row.label}
                        </td>
                        {/* Value cells */}
                        {variants.map((v, vi) => {
                          const val = values[vi];
                          const isCurrent = v.id === currentId;
                          // For monthly total: highlight best (green) and worst (red)
                          let valStyle = { color: _NAVY, fontWeight: 500 };
                          if (row.key === 'monthly_total') {
                            const t = displayTotal(v);
                            if (t != null && minTotal !== maxTotal) {
                              if (t === minTotal) valStyle = { color: '#15803d', fontWeight: 700 };
                              else if (t === maxTotal) valStyle = { color: '#b91c1c', fontWeight: 700 };
                            }
                          } else if (diff && !isCurrent) {
                            valStyle = { color: '#b45309', fontWeight: 500 };
                          }
                          return (
                            <td key={v.id}
                              style={{
                                padding: '9px 16px',
                                background: isCurrent ? 'rgb(219 234 254 / 0.3)' : 'transparent',
                                borderLeft: isCurrent ? '1px solid #bfdbfe' : 'none',
                                borderRight: isCurrent ? '1px solid #bfdbfe' : 'none',
                              }}>
                              <span style={valStyle}>{val}</span>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* Pricing breakdown rows — line items from snapshot */}
              {(() => {
                // Collect all unique line item labels across variants
                const allLabels = [];
                const labelSet = new Set();
                variants.forEach(v => {
                  (v.pricing_snapshot?.line_items || [])
                    .filter(li => !li.is_included && (li.monthly_amount || 0) > 0)
                    .forEach(li => {
                      const key = `${li.category}::${li.label}`;
                      if (!labelSet.has(key)) { labelSet.add(key); allLabels.push({ key, label: li.label, category: li.category }); }
                    });
                });
                if (!allLabels.length) return null;
                return (
                  <React.Fragment>
                    <tr>
                      <td colSpan={variants.length + 1}
                        style={{ padding: '8px 16px 4px', background: '#f8fafc',
                                 borderBottom: '1px solid #e2e8f0', borderTop: '2px solid #e2e8f0' }}>
                        <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#1e40af' }}>
                          Pricing Breakdown
                        </span>
                      </td>
                    </tr>
                    {allLabels.map(({ key, label }, ri) => {
                      const vals = variants.map(v => {
                        const li = (v.pricing_snapshot?.line_items || []).find(l => `${l.category}::${l.label}` === key);
                        return li ? `${sy(v.currency)}${fmtN(displayLineAmount(v, li))}` : '—';
                      });
                      const diff2 = new Set(vals).size > 1;
                      return (
                        <tr key={key} style={{ background: diff2 ? 'rgb(251 191 36 / 0.07)' : (ri % 2 === 0 ? 'white' : '#f8fafc'), borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '7px 16px', fontSize: '12px', color: 'hsl(var(--muted-fg))', fontWeight: 500,
                            position: 'sticky', left: 0, zIndex: 1,
                            background: diff2 ? 'rgb(251 191 36 / 0.07)' : (ri % 2 === 0 ? 'white' : '#f8fafc') }}>
                            {diff2 && <span className="mr-1" style={{ color: '#d97706' }}>◈</span>}
                            {label}
                          </td>
                          {variants.map((v, vi) => {
                            const isCurrent = v.id === currentId;
                            return (
                              <td key={v.id} style={{ padding: '7px 16px', fontSize: '12px',
                                color: diff2 && vi !== variants.findIndex(vv => vv.id === currentId) ? '#b45309' : _NAVY,
                                fontWeight: diff2 ? 600 : 400,
                                background: isCurrent ? 'rgb(219 234 254 / 0.3)' : 'transparent',
                                borderLeft: isCurrent ? '1px solid #bfdbfe' : 'none',
                                borderRight: isCurrent ? '1px solid #bfdbfe' : 'none',
                              }}>
                                {vals[vi]}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })()}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 flex items-center justify-between"
          style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
            <span style={{ color: '#d97706' }}>◈</span> = differs across variants &nbsp;·&nbsp;
            <span style={{ color: '#15803d', fontWeight: 700 }}>Green</span> = lowest monthly total &nbsp;·&nbsp;
            <span style={{ color: '#b91c1c', fontWeight: 700 }}>Red</span> = highest monthly total
          </p>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, isManager } = useAuth();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await quotesAPI.get(id);
      setQuote(data);
    } catch { setErr('Quote not found or access denied.'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const isOwner = quote && (
    quote.owner_user_id === user?.id ||
    isAdmin ||
    isManager ||
    quote.psm_user_id === user?.id
  );
  const canEdit = isOwner && !quote?.is_legacy_model && quote?.status !== 'ACCEPTED';
  const snap = quote?.pricing_snapshot;
  const currency = quote?.currency || 'GBP';
  const allSnapLines = snap ? (snap.line_items || []) : [];
  const billable = allSnapLines.filter(l => !l.is_included);
  const fmtN     = (n) => Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const productLabel = quote
    ? (quote.quote_category === 'TEST_SYSTEM' || quote.quote_category === 'BESPOKE' || quote.quote_category === 'MANUAL')
        ? `C4P Custom${quote.quote_category === 'TEST_SYSTEM' ? ' - Test System' : ''}`
        : `C4P ${({'STANDARD':'Standard','ADVANCED':'Advanced','MTPRO':'MTPro'}[quote.product_code] || quote.product_code)}`
    : '';
  const isCustomQuote = quote?.quote_category === 'TEST_SYSTEM' || quote?.quote_category === 'BESPOKE' || quote?.quote_category === 'MANUAL';
  const isCustomZeroUsers = isCustomQuote && (quote?.num_users || 0) === 0;
  const hasDbServer = !!(quote?.db_server || (quote?.extra_db_servers?.length > 0));
  const dbTypeLabel      = (t) => ({ SQL: 'Microsoft SQL Server', PRO4GL: 'Progress 4GL' }[t] || 'SAP HANA');
  const dbTypeLabelShort = (t) => ({ SQL: 'SQL Server', PRO4GL: 'Progress 4GL' }[t] || 'SAP HANA');
  // Custom quotes can have multiple DB servers of different types (db_server + extra_db_servers) -
  // combine the distinct types the same way the wizard's live summary does ("Progress 4GL and SAP HANA").
  const customDbTypesDisplay = (labelFn) => {
    const types = [quote?.db_server?.db_type, ...(quote?.extra_db_servers || []).map(s => s.db_type)].filter(Boolean);
    const uniq = [...new Set(types)];
    return uniq.length ? uniq.map(labelFn).join(' and ') : labelFn(quote?.db_type || 'HANA');
  };
  const isTenantDirect = quote?.billing_model === 'TENANT_DIRECT';
  // For Tenant Direct, the displayed total is the grossed-up tenant invoice; otherwise C4P base
  const displayTotal = isTenantDirect && snap?.tenant_invoice_monthly
    ? snap.tenant_invoice_monthly
    : snap?.monthly_total ?? 0;
  const vat    = currency === 'GBP' && snap ? Math.round(displayTotal * 0.20 * 100) / 100 : 0;
  const incVat = snap ? displayTotal + vat : 0;
  const BILLING_LABELS = { PARTNER: 'Partner Billing', DIRECT: 'Direct (No Tenant)', TENANT_DIRECT: 'Tenant Direct' };
  const psmName = quote?.psm_user_name || '-';

  const handleStatusChange = async (newStatus) => {
    try {
      await quotesAPI.updateStatus(id, newStatus);
      toast.success(`Status → ${newStatus}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Manual contract conversion
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertStartDate, setConvertStartDate] = useState('');
  const [converting, setConverting] = useState(false);
  const isManualQuote = quote?.quote_category === 'MANUAL';
  // A CR quote is explicitly flagged at creation time — not just any accepted quote with a contract_id
  // (new quotes also get contract_id written back after signing, so we must not rely on that alone)
  const isCrQuote = !!(quote?.is_change_request);
  // Only fresh (non-CR) manual quotes show the "Convert to Contract" flow
  const isNewManualQuote = isManualQuote && !isCrQuote;
  const hasDealRef = !!(quote?.deal_ref);

  // Variant group loading
  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [savingVariant, setSavingVariant] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  const loadVariants = useCallback(async () => {
    if (!quote?.variant_group_id) { setVariants([]); return; }
    setVariantsLoading(true);
    try {
      const { data } = await quotesAPI.getVariants(quote.id);
      setVariants(data || []);
    } catch { setVariants([]); }
    finally { setVariantsLoading(false); }
  }, [quote?.variant_group_id, quote?.id]);

  useEffect(() => { loadVariants(); }, [loadVariants]);

  // Derive this quote's own variant label from the group (v1, v2, …)
  const myVariant = variants.find(v => v.id === id);
  const isInVariantGroup = !!(quote?.variant_group_id);

  const handleSaveAsVariant = async () => {
    setSavingVariant(true);
    try {
      const { data: q } = await quotesAPI.saveAsVariant(id);
      toast.success(`Variant ${q.quote_number} created`);
      navigate(`/quotes/${q.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create variant'); }
    finally { setSavingVariant(false); }
  };

  const handleSetVariantPrimary = async (variantId) => {
    try {
      await quotesAPI.setVariantPrimary(variantId);
      await load();
      await loadVariants();
      toast.success('Primary variant updated');
    } catch { toast.error('Failed to update primary variant'); }
  };

  const handleConvertToContract = async () => {
    setConverting(true);
    try {
      const { data: res } = await contractsAPI.convertManualQuote(id, convertStartDate || null);
      toast.success(`Contract ${res.contract_number} created successfully`);
      setShowConvertModal(false);
      navigate(`/contracts/${res.contract_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to convert to contract');
    } finally {
      setConverting(false);
    }
  };

  // Signing
  const [showSignModal, setShowSignModal]     = useState(false);

  // AI Summarise
  const [aiSummarising, setAiSummarising] = useState(false);
  const [summaryText, setSummaryText]     = useState('');
  const [summaryDialog, setSummaryDialog] = useState(false);

  const handleAISummarise = async () => {
    if (!snap) return toast.error('Save the quote first to generate a summary');
    setAiSummarising(true);
    try {
      const token = localStorage.getItem('c4p_access_token');
      const res = await fetch(`${API_BASE}/ai/summarise-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quote_id: id }),
      });
      const r = await res.json();
      if (!res.ok) throw new Error(r.detail || 'AI failed');
      setSummaryText(r.summary);
      setSummaryDialog(true);
    } catch (e) { toast.error(e.message || 'AI summary failed'); }
    finally { setAiSummarising(false); }
  };
  const [signEmail, setSignEmail]             = useState('');
  const [signName, setSignName]               = useState('');
  const [sendEmail, setSendEmail]             = useState(true);
  const [signingLink, setSigningLink]         = useState('');
  const [signingBusy, setSigningBusy]         = useState(false);
  const [signatureProof, setSignatureProof]   = useState(null);
  const [signingEmailAllowed, setSigningEmailAllowed] = useState(true);
  const [msaBlocker, setMsaBlocker]           = useState(null); // { blocker, name }

  // Derive MSA pre-flight status from the loaded quote data.
  // These fields are enriched by the backend on GET /quotes/{id}.
  const msaPreFlight = quote ? (() => {
    const partnerSigned = quote.customer_msa_signed === true;
    const tenantNeedsMsa = quote.billing_model === 'TENANT_DIRECT' || quote.tenant_billing_via_partner === false;
    const tenantSigned = quote.tenant_msa_signed === true;
    if (!partnerSigned) {
      return { blocker: 'partner', name: quote.customer_name || 'Partner',
               message: 'Partner MSA has not been recorded as signed.',
               billing_via_partner: undefined };
    }
    if (tenantNeedsMsa && !tenantSigned) {
      const reason = quote.billing_model === 'TENANT_DIRECT'
        ? 'This quote uses Tenant Direct billing. A Super Admin must record the Tenant MSA before this quote can be sent.'
        : 'This tenant is configured for Direct Billing and requires its own MSA. A Super Admin must record the Tenant MSA.';
      return { blocker: 'tenant', name: quote.tenant_name || 'Tenant',
               message: reason,
               billing_via_partner: quote.tenant_billing_via_partner };
    }
    return null; // All clear
  })() : null;

  // Load signing email enabled setting - only restricts PSMs, not admins/managers
  useEffect(() => {
    if (isAdmin || isManager) return; // admins/managers can always send
    adminAPI.getSigningEmailEnabled()
      .then(r => {
        setSigningEmailAllowed(r.data.enabled);
        if (!r.data.enabled) setSendEmail(false);
      })
      .catch(() => {});
  }, [isAdmin, isManager]);

  // Load signature proof if quote is ACCEPTED
  useEffect(() => {
    if (quote?.status === 'ACCEPTED') {
      const token = localStorage.getItem('c4p_access_token');
      fetch(`${API_BASE}/quotes/${id}/signature`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setSignatureProof(d); })
        .catch(() => {});
    }
  }, [id, quote?.status]);

  const openSignModal = () => {
    // MSA pre-flight check — if partner/tenant MSA is missing, show blocker immediately
    // without requiring the user to enter signer details first.
    if (msaPreFlight) {
      setMsaBlocker(msaPreFlight);
      setSigningLink('');
      setShowSignModal(true);
      return;
    }

    const isTenantDirect = quote?.billing_model === 'TENANT_DIRECT';
    const tenantId       = quote?.tenant_id;
    const tenantContacts = quote?.tenant_contacts || [];          // normalised in get_quote
    const contactIds     = quote?.contact_ids || [];
    const customerContacts = quote?.customer_contacts || [];

    // For Tenant Direct: the signing link is sent to the TENANT, not the partner.
    // The wizard stores 'tenant-{id}' in contact_ids when the tenant contact is selected.
    const tenantContactSelected =
      isTenantDirect && tenantId && contactIds.includes(`tenant-${tenantId}`);

    let primary = null;
    if (tenantContactSelected || (isTenantDirect && tenantContacts.length > 0)) {
      // Use first available tenant contact
      primary = tenantContacts[0] || null;
    } else {
      // Partner / Direct billing — use partner contacts as before
      const selected = customerContacts.filter(c => contactIds.includes(c.id) && c.email);
      primary = selected[0]
        || customerContacts.find(c => c.is_primary && c.email)
        || customerContacts.find(c => c.email)
        || null;
    }

    setSignEmail(primary?.email || '');
    setSignName(primary?.name || '');
    setSigningLink('');
    setMsaBlocker(null);
    setShowSignModal(true);
  };

  const handleCreateSigningLink = async () => {
    setSigningBusy(true);
    setMsaBlocker(null);
    try {
      const token = localStorage.getItem('c4p_access_token');
      const res = await fetch(`${API_BASE}/quotes/${id}/signing-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-App-Url': process.env.REACT_APP_BACKEND_URL },
        body: JSON.stringify({ recipient_email: signEmail, recipient_name: signName, send_email: sendEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        // MSA gate: show inline blocking message rather than a toast
        const detail = data.detail;
        if (res.status === 422 && detail && detail.code === 'MSA_NOT_SIGNED') {
          setMsaBlocker(detail);
          return;
        }
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to create signing link');
        return;
      }
      setSigningLink(data.signing_url);
      if (sendEmail && data.email_sent) toast.success('Signing link emailed to partner');
      load(); // refresh quote status
    } catch { toast.error('Failed to create signing link'); }
    finally { setSigningBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(signingLink);
    toast.success('Link copied to clipboard');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await quotesAPI.delete(id); toast.success('Quote deleted'); navigate('/quotes'); }
    catch { toast.error('Delete failed'); setDeleting(false); }
  };

  const handleSaveAsNew = async () => {
    try {
      const { data: q } = await quotesAPI.saveAsNew(id);
      toast.success(`New quote ${q.quote_number} created`);
      navigate(`/quotes/${q.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDownloadPDF = async (variant = 'tenant') => {
    if (!snap && variant !== 'signed') return toast.error('No saved pricing snapshot');
    const endpoint = variant === 'partner'
      ? `${API_BASE}/quotes/${id}/partner-summary-pdf`
      : variant === 'signed'
        ? `${API_BASE}/quotes/${id}/signed-pdf`
        : `${API_BASE}/quotes/${id}/pdf`;
    const label = variant === 'partner' ? 'Partner Summary' : variant === 'signed' ? 'Signed Quote' : 'Quote';
    const tid = toast.loading(`Generating ${label} PDF…`);
    try {
      const token = localStorage.getItem('c4p_access_token');
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Error');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const rawName = quote.quote_name || quote.quote_number;
      const safeName = rawName.replace(/[\\/:*?"<>|·•]/g, '-').replace(/\s+-\s+/g, ' - ').trim();
      const suffix = variant === 'partner' ? '-partner-summary' : variant === 'signed' ? '-SIGNED' : '';
      const a = document.createElement('a'); a.href = url; a.download = `${safeName}${suffix}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.dismiss(tid); toast.success(`${label} PDF downloaded`);
    } catch (e) { toast.dismiss(tid); toast.error(e.message); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-16 w-full" /><Skeleton className="h-48 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (err) return <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>;
  if (!quote) return null;

  // Legacy model detection (exclude MANUAL quotes from the heuristic check)
  const isLegacy = quote.is_legacy_model || (!isManualQuote && !quote.db_server && !quote.app_servers?.length && quote.product_code === 'ADVANCED');

  return (
    <div className="flex flex-col min-h-full" style={{ background: '#f8fafc' }} data-testid="quote-detail-page">

      {/* ── Sticky Header ── */}
      <div className="bg-white flex-shrink-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
        <div className="px-8 pt-6 pb-4">
          {/* Breadcrumb */}
          <button className="flex items-center gap-1.5 text-xs font-medium mb-4 hover:opacity-70 transition-opacity"
            style={{ color: 'hsl(var(--muted-fg))' }} data-testid="quote-back-button"
            onClick={() => location.key !== 'default' ? navigate(-1) : navigate('/quotes')}>
            <ArrowLeft size={13} /> Quotes
          </button>
          {/* Identity + actions row */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            {/* Left: identity */}
            <div className="flex flex-col gap-1.5 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
                  {quote.quote_number}
                </h1>
                <StatusBadge status={quote.status} />
                <span className="text-xs px-1.5 py-0.5 rounded font-mono"
                  style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}>v{quote.version}</span>
                {/* Variant label — shown when this quote is part of a variant group */}
                {isInVariantGroup && myVariant && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1"
                    style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}
                    data-testid="variant-label-badge">
                    <GitBranch size={10} />
                    {myVariant.variant_label}
                    {myVariant.is_primary_deal_quote && (
                      <span className="ml-0.5 opacity-75">· Primary</span>
                    )}
                  </span>
                )}
                {isManualQuote && (
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded"
                    style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' }}>
                    Manual Pricing
                  </span>
                )}
                {hasDealRef && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1"
                    style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}
                    data-testid="deal-ref-badge">
                    Deal: {quote.deal_ref}{quote.deal_probability != null && <span style={{ opacity: 0.7 }}> · {quote.deal_probability}%</span>}
                  </span>
                )}
              </div>
              {quote.quote_name && (
                <p className="text-base font-semibold" style={{ color: NAVY }} data-testid="quote-detail-name">
                  {quote.quote_name}
                </p>
              )}
              <p className="text-xs flex items-center gap-1.5 flex-wrap" style={{ color: 'hsl(var(--muted-fg))' }}>
                <span>{quote.owner_name}</span>
                <span>·</span>
                <span>{new Date(quote.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                {quote.region_name && <><span>·</span><span>{quote.region_name}</span></>}
              </p>
            </div>
            {/* Right: monthly total + actions */}
            <div className="flex flex-col items-end gap-3 flex-shrink-0">
              {snap && displayTotal > 0 && (
                <div className="text-right">
                  <p className="text-xs font-semibold uppercase tracking-widest mb-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>Monthly Total</p>
                  <p className="text-3xl font-light tabular-nums" style={{ fontFamily: 'var(--font-display)', color: NAVY }}>
                    {sym(currency)}{fmtN(displayTotal)}
                  </p>
                  {currency === 'GBP' && incVat > 0 && (
                    <p className="text-sm mt-0.5 tabular-nums" style={{ color: '#d97706' }}>
                      {sym(currency)}{fmtN(incVat)} inc. VAT
                    </p>
                  )}
                </div>
              )}
              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {quote.status === 'EXPIRED' && isOwner && (
                  <Button size="sm" onClick={handleSaveAsNew} className="gap-1.5"
                    style={{ background: ORANGE, color: 'white' }} data-testid="quote-save-as-new-expired">
                    <Copy size={13} /> Save As New
                  </Button>
                )}
                {snap && quote.status !== 'EXPIRED' && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleAISummarise} disabled={aiSummarising} className="gap-1.5" data-testid="quote-summarise-button">
                      {aiSummarising ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Summarise
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPDF('tenant')} className="gap-1.5" data-testid="quote-download-pdf-button">
                      <FileDown size={13} /> {isTenantDirect ? 'Tenant PDF' : 'PDF'}
                    </Button>
                    {isTenantDirect && (
                      <Button variant="outline" size="sm" onClick={() => handleDownloadPDF('partner')} className="gap-1.5"
                        style={{ borderColor: 'hsl(265 89% 68%)', color: 'hsl(265 89% 45%)' }} data-testid="quote-download-partner-pdf-button">
                        <FileDown size={13} /> Partner PDF
                      </Button>
                    )}
                    {quote.status === 'ACCEPTED' && signatureProof && (
                      <Button size="sm" onClick={() => handleDownloadPDF('signed')} className="gap-1.5"
                        style={{ background: '#16a34a', color: 'white' }} data-testid="quote-download-signed-pdf-button">
                        <Shield size={13} /> Signed PDF
                      </Button>
                    )}
                  </>
                )}
                {canEdit && quote.status !== 'EXPIRED' && (
                  <Link to={`/quotes/${id}/edit`}>
                    <Button size="sm" style={{ background: NAVY, color: 'white' }} className="gap-1.5" data-testid="quote-edit-button">
                      <Pencil size={13} /> Edit Quote
                    </Button>
                  </Link>
                )}
                {isOwner && quote.status !== 'EXPIRED' && (
                  <>
                    <Button variant="outline" size="sm" onClick={handleSaveAsNew} className="gap-1.5" data-testid="quote-save-as-new-button">
                      <Copy size={13} /> Save As New
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleSaveAsVariant} disabled={savingVariant} className="gap-1.5"
                      style={{ borderColor: '#1e40af', color: '#1e40af' }}
                      data-testid="quote-save-as-variant-button">
                      {savingVariant ? <Loader2 size={13} className="animate-spin" /> : <GitBranch size={13} />}
                      Save as Variant
                    </Button>
                    {(quote.status === 'DRAFT' || quote.status === 'SENT') && (
                      <Button size="sm" onClick={openSignModal} className="gap-1.5"
                        style={{ background: '#16a34a', color: 'white' }} data-testid="quote-send-for-signing">
                        <PenLine size={13} /> Send for Signing
                      </Button>
                    )}
                    {isNewManualQuote && isAdmin && (quote.status === 'DRAFT' || quote.status === 'SENT') && (
                      <Button size="sm" onClick={() => { setConvertStartDate(''); setShowConvertModal(true); }} className="gap-1.5"
                        style={{ background: '#ea580c', color: 'white' }} data-testid="quote-convert-to-contract">
                        <Building2 size={13} /> Convert to Contract
                      </Button>
                    )}
                    {(quote.status !== 'ACCEPTED' || isAdmin) && (
                      <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(true)} style={{ color: 'hsl(var(--danger))' }}><Trash2 size={13} /></Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content area ── */}
      <div className="flex-1 px-8 py-8 space-y-5">

        {isLegacy && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This quote was created with an earlier pricing model. Use <strong>Save As New</strong> to create an updated quote with the current pricing structure.
            </AlertDescription>
          </Alert>
        )}

        {/* MSA pre-flight warning — shown persistently so PSMs see the blocker before clicking */}
        {msaPreFlight && (quote.status === 'DRAFT' || quote.status === 'SENT') && !isCrQuote && (
          <div className="rounded-xl border px-4 py-3 flex items-start gap-3" data-testid="msa-preflight-banner"
            style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
            <AlertCircle size={15} style={{ color: '#dc2626', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: '#991b1b' }}>
                Cannot send for signing — MSA not recorded
              </p>
              <p className="text-sm mt-0.5" style={{ color: '#7f1d1d' }}>
                <strong>{msaPreFlight.name}</strong>'s MSA has not been recorded as signed.{' '}
                {msaPreFlight.blocker === 'partner'
                  ? 'A Super Admin must record the Partner MSA before this quote can be sent for signing.'
                  : msaPreFlight.billing_via_partner === false
                    ? 'This tenant is configured for Direct Billing and requires its own MSA. A Super Admin must record the Tenant MSA.'
                    : 'This quote uses Tenant Direct billing. A Super Admin must record the Tenant MSA before sending for signing.'}
              </p>
            </div>
          </div>
        )}

      {/* Change Request context banner */}
      {isCrQuote && quote.status !== 'ACCEPTED' && (
        <div className="rounded-xl border px-4 py-3 flex items-start gap-3" data-testid="cr-context-banner"
          style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
          <FileSignature size={16} style={{ color: '#1d4ed8', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#1e40af' }}>
              Change Request for Contract{' '}
              <a href={`/contracts/${quote.contract_id || quote.order_id}`}
                target="_blank" rel="noopener noreferrer"
                className="underline" style={{ color: '#1d4ed8' }}>
                {quote.contract_number_ref || quote.order_number_ref || 'View Contract'}
              </a>
            </p>
            <p className="text-sm mt-0.5" style={{ color: '#1e3a8a' }}>
              {isManualQuote && isAdmin
                ? 'Review pricing on the Pricing step (Step 8) if needed, then send for signing. Once signed, the contract will be updated automatically.'
                : 'Send this quote for signing to update the contract. The signer will receive a link by email.'
              }
            </p>
          </div>
        </div>
      )}

      {/* Variant group panel — shown when this quote is part of a variant group */}
      {isInVariantGroup && variants.length > 0 && (
        <div className="rounded-xl border overflow-hidden" data-testid="variant-group-panel"
          style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
          <div className="px-4 py-2.5 flex items-center gap-2"
            style={{ borderBottom: '1px solid #e2e8f0', background: '#eff6ff' }}>
            <GitBranch size={13} style={{ color: '#1e40af' }} />
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#1e40af' }}>
              Quote Variants — {myVariant?.variant_label?.split(' v')[0] || quote.quote_number}
            </p>
            <span className="text-xs ml-auto" style={{ color: '#3b82f6' }}>{variants.length} variant{variants.length !== 1 ? 's' : ''}</span>
            {variants.length >= 2 && (
              <Button variant="outline" size="sm" onClick={() => setShowCompare(true)}
                className="gap-1.5 h-6 px-2.5 text-xs ml-1 flex-shrink-0"
                style={{ borderColor: '#1e40af', color: '#1e40af', background: 'white' }}
                data-testid="compare-variants-button">
                <GitBranch size={10} /> Compare
              </Button>
            )}
          </div>
          {variantsLoading ? (
            <div className="px-4 py-3"><div className="h-8 rounded bg-slate-100 animate-pulse" /></div>
          ) : (
            <div className="divide-y" style={{ borderColor: '#e2e8f0' }}>
              {variants.map(v => (
                <div key={v.id}
                  className="flex items-center justify-between px-4 py-2.5"
                  style={{ background: v.id === id ? '#eff6ff' : 'white' }}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{ background: '#dbeafe', color: '#1e40af', fontFamily: 'var(--font-mono, monospace)' }}>
                      {v.variant_label}
                    </span>
                    {v.id !== id ? (
                      <a href={`/quotes/${v.id}`} className="text-xs font-mono hover:underline flex-shrink-0"
                        style={{ color: 'hsl(var(--muted-fg))' }}>
                        {v.quote_number}
                      </a>
                    ) : (
                      <span className="text-xs font-mono flex-shrink-0" style={{ color: 'hsl(var(--muted-fg))' }}>
                        {v.quote_number} (this quote)
                      </span>
                    )}
                    <span className="text-xs px-1.5 py-0.5 rounded"
                      style={{
                        background: v.status === 'ACCEPTED' ? '#f0fdf4' : v.status === 'SENT' ? '#f0f9ff' : '#f8fafc',
                        color:      v.status === 'ACCEPTED' ? '#15803d' : v.status === 'SENT' ? '#0c4a6e' : '#475569',
                        border:     `1px solid ${v.status === 'ACCEPTED' ? '#bbf7d0' : v.status === 'SENT' ? '#bae6fd' : '#e2e8f0'}`,
                      }}>
                      {v.status}
                    </span>
                    {v.is_primary_deal_quote && (
                      <span className="text-xs px-1.5 py-0.5 rounded flex items-center gap-1"
                        style={{ background: '#fefce8', color: '#854d0e', border: '1px solid #fde68a' }}>
                        <Star size={9} fill="currentColor" /> Primary
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-semibold tabular-nums" style={{ color: NAVY }}>
                      {v.pricing_snapshot
                        ? `${({ GBP: '£', EUR: '€', USD: '$', CAD: 'CA$', AUD: 'A$' }[v.currency] || '')}${Number(v.pricing_snapshot.monthly_total || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo`
                        : '—'}
                    </span>
                    {!v.is_primary_deal_quote && isOwner && (v.status === 'DRAFT' || v.status === 'SENT') && (
                      <Button variant="outline" size="sm" onClick={() => handleSetVariantPrimary(v.id)}
                        className="text-xs gap-1 h-6 px-2"
                        style={{ borderColor: '#d97706', color: '#d97706' }}
                        data-testid={`set-primary-${v.id}`}>
                        Set Primary
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Manual contract info banner — only for fresh (non-CR) manual quotes */}
      {isNewManualQuote && quote.status !== 'ACCEPTED' && (
        <div className="rounded-xl border px-4 py-3 flex items-start gap-3" data-testid="manual-quote-banner"
          style={{ background: '#fff7ed', borderColor: '#fdba74' }}>
          <Building2 size={16} style={{ color: '#ea580c', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#9a3412' }}>C4P Custom — Manual Pricing</p>
            <p className="text-sm mt-0.5" style={{ color: '#7c2d12' }}>
              {isAdmin
                ? 'Prices on this quote have been manually set. Use the orange "Convert to Contract" button to create a live contract directly with no signature — or click "Send for Signing" if the customer needs to digitally accept it first, which will automatically create the contract once signed.'
                : 'This quote contains bespoke pricing for a legacy or existing customer. Click "Send for Signing" to have the customer digitally accept it, or ask a Super Admin to convert it directly to a contract without a signature.'
              }
            </p>
          </div>
        </div>
      )}

      {/* Expired quote warning banner */}
      {quote.status === 'EXPIRED' && (
        <div className="rounded-xl border px-4 py-3 flex items-start gap-3" data-testid="expired-quote-banner"
          style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
          <AlertCircle size={16} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: '#92400e' }}>This quote has expired</p>
            <p className="text-sm mt-0.5" style={{ color: '#78350f' }}>
              Expired quotes cannot be sent for signing or downloaded as PDF. Use <strong>Save As New</strong> to create
              an updated quote — it will automatically pick up the latest pricing.
            </p>
          </div>
        </div>
      )}

      {/* Digitally Accepted Banner */}
      {quote.status === 'ACCEPTED' && signatureProof && (
        <div className="rounded-xl overflow-hidden shadow-sm" style={{ background: 'linear-gradient(135deg, #14532d 0%, #16a34a 100%)' }}
          data-testid="signature-proof-banner">
          <div className="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
                  <Shield size={20} color="white" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.7)' }}>Digitally Accepted</p>
                  <p className="font-bold text-white text-lg">{signatureProof.signer_name}</p>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>{signatureProof.signer_email}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Signed</p>
                <p className="text-sm font-semibold text-white">
                  {new Date(signatureProof.signed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {new Date(signatureProof.signed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} UTC
                </p>
              </div>
            </div>
            <div className="mt-4 pt-4 flex items-center gap-6 flex-wrap" style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              {signatureProof.signature_image && (
                <div className="bg-white rounded-lg p-2">
                  <img src={signatureProof.signature_image} alt="Signature" className="h-12 object-contain" />
                </div>
              )}
              <div className="text-xs space-y-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                <p>IP Address: {signatureProof.ip_address}</p>
                <p>Quote v{signatureProof.quote_version} · {signatureProof.quote_number}</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Proof of acceptance on record</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {quote.status === 'ACCEPTED' && !signatureProof && (
        <div className="rounded-xl p-4 flex items-center gap-3" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <CheckCircle2 size={18} style={{ color: '#16a34a' }} />
          <p className="text-sm font-medium" style={{ color: '#14532d' }}>Accepted</p>
        </div>
      )}

      {/* Contract link banner - shown when this quote has been converted to / has updated a contract */}
      {quote.status === 'ACCEPTED' && (quote.contract_id || quote.order_id) && (
        <div className="rounded-xl p-4 flex items-center justify-between gap-3"
          style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
          data-testid="quote-contract-banner">
          <div className="flex items-center gap-3">
            <PackageCheck size={18} style={{ color: '#1d4ed8' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: '#1e3a5f' }}>
                {isCrQuote
                  ? 'Contract updated — this Change Request has been applied'
                  : 'This quote has been converted to a Live Contract'
                }
              </p>
              {(quote.contract_number_ref || quote.order_number_ref) && (
                <p className="text-xs mt-0.5" style={{ color: '#3b82f6' }}>
                  Contract: {quote.contract_number_ref || quote.order_number_ref}
                </p>
              )}
              {quote.po_number && (
                <p className="text-xs mt-0.5 font-mono font-semibold" style={{ color: '#1d4ed8' }}>
                  PO: {quote.po_number}
                </p>
              )}
            </div>
          </div>
          <a href={`/contracts/${quote.contract_id || quote.order_id}`} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="outline" className="gap-1.5 flex-shrink-0"
              style={{ borderColor: '#93c5fd', color: '#1d4ed8' }}
              data-testid="quote-view-contract-btn">
              View Contract
            </Button>
          </a>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: section cards */}
        <div className="lg:col-span-2 space-y-3">
          <SectionCard icon={User} iconBg="#eff6ff" iconColor="#1d4ed8" title="Partner"
            editStep={1} quoteId={id} isOwner={canEdit}
            value={
              <div className="space-y-0.5">
                <p className="font-semibold">{quote.customer_name || quote.customer_id}</p>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {BILLING_LABELS[quote.billing_model] || 'Partner Billing'}
                </p>
                {quote.billing_model !== 'DIRECT' && quote.end_customer_name && (
                  <p className="text-sm">
                    <span style={{ color: 'hsl(var(--muted-fg))' }}>Tenant: </span>
                    <span className="font-medium">{quote.end_customer_name}</span>
                  </p>
                )}
                <p className="text-sm">
                  <span style={{ color: 'hsl(var(--muted-fg))' }}>PSM: </span>
                  <span className="font-medium">{psmName}</span>
                </p>
                {isTenantDirect && snap?.partner_margin_monthly > 0 && (() => {
                  const pupmMargin = (snap.partner_margin_pupm || 0) * (quote.num_users || 0);
                  const nonUserMargin = snap.partner_margin_monthly - pupmMargin;
                  return (
                    <div className="mt-1 space-y-0.5">
                      <p className="text-xs" style={{ color: 'hsl(265 89% 45%)' }}>
                        Partner margin: {sym(currency)}{fmtN(snap.partner_margin_monthly)}/mo
                        {' · '}C4P net: {sym(currency)}{fmtN(snap.c4p_net_monthly)}/mo
                      </p>
                      {pupmMargin > 0 && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>PUPM: {sym(currency)}{fmtN(snap.partner_margin_pupm)}/user × {quote.num_users} = {sym(currency)}{fmtN(pupmMargin)}</p>}
                      {nonUserMargin > 0.005 && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Infrastructure/extras: {snap.partner_margin_percent}% = {sym(currency)}{fmtN(nonUserMargin)}</p>}
                    </div>
                  );
                })()}
                {quote.notes && <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{quote.notes}</p>}
              </div>
            } />

          <SectionCard icon={Globe} iconBg="#f0fdf4" iconColor="#15803d" title="Setup"
            editStep={2} quoteId={id} isOwner={canEdit}
            value={
              <div className="space-y-1 text-sm">
                <p>
                  <span className="font-semibold">{quote.region_name || 'No region set'}</span>
                  <span style={{ color: 'hsl(var(--muted-fg))' }}> · {quote.currency}</span>
                </p>
                {quote.billing_frequency && quote.billing_frequency !== 'Monthly' && (
                  <p style={{ color: 'hsl(var(--muted-fg))' }}>Billing: {quote.billing_frequency}</p>
                )}
                {quote.payment_method && (
                  <p style={{ color: 'hsl(var(--muted-fg))' }}>Payment: {quote.payment_method}</p>
                )}
                {quote.po_number && (
                  <p style={{ color: 'hsl(var(--muted-fg))' }}>
                    PO: <span className="font-mono font-semibold" style={{ color: 'hsl(var(--c4p-navy-900))' }}>{quote.po_number}</span>
                  </p>
                )}
                {quote.use_next_available_slot
                  ? <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Build: Next available slot</p>
                  : quote.required_by_date && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Required by {new Date(quote.required_by_date).toLocaleDateString('en-GB')}</p>
                }
                {quote.expiry_date && <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>Valid until {new Date(quote.expiry_date).toLocaleDateString('en-GB')}</p>}
              </div>
            } />

          <SectionCard icon={Package} iconBg="#fff7ed" iconColor="#c2410c" title="Product & Scale"
            editStep={3} quoteId={id} isOwner={canEdit}
            value={
              <div className="space-y-1 text-sm">
                <p>
                  <span className="font-semibold">{productLabel}</span>{!isCustomZeroUsers && ` · ${quote.num_users} users`} · {quote.term_months} months
                </p>
                {snap?.user_tier_label && !isCustomZeroUsers && <p style={{ color: ORANGE }}>{snap.user_tier_label} · {sym(currency)}{snap.pupm_price}/user/month</p>}
              </div>
            } />

          {(quote.db_server || (quote.db_type && !(isCustomQuote && !hasDbServer))) && (
            <SectionCard icon={Database} iconBg="#f5f3ff" iconColor="#7c3aed" title="Database"
              editStep={quote.product_code === 'STANDARD' ? 3 : 4} quoteId={id} isOwner={canEdit}
              value={
                <div className="text-sm space-y-1">
                  {quote.db_server ? (
                    <>
                      <p className="font-semibold">{quote.db_server.server_description}</p>
                      <p style={{ color: 'hsl(var(--muted-fg))' }}>
                        {isCustomQuote ? customDbTypesDisplay(dbTypeLabel) : dbTypeLabel(quote.db_server.db_type)}
                        {!isCustomQuote && <>{' · '}{quote.db_server.included_data_gb}GB Data + {quote.db_server.included_backup_gb}GB Backup included</>}
                        {quote.db_server.additional_data_gb > 0 && ` · +${quote.db_server.additional_data_gb}GB extra data`}
                        {quote.db_server.additional_backup_gb > 0 && ` · +${quote.db_server.additional_backup_gb}GB extra backup`}
                      </p>
                    </>
                  ) : (
                    <p className="font-semibold">
                      {dbTypeLabel(quote.db_type || 'HANA')}
                    </p>
                  )}
                </div>
              } />
          )}

          {quote.app_servers?.length > 0 && (
            <SectionCard icon={Server} iconBg="#f0fdf4" iconColor="#15803d" title="Application Servers"
              editStep={5} quoteId={id} isOwner={canEdit}
              value={
                <div className="space-y-2 text-sm">
                  {quote.app_servers.map((s, i) => (
                    <div key={i} className="space-y-0.5">
                      <p style={{ color: s.is_included_in_pupm ? 'hsl(var(--muted-fg))' : 'inherit' }}>
                        {s.server_description || s.server_code}
                        {s.is_included_in_pupm && ' (included)'}
                        {s.additional_root_gb > 0 && ` · +${s.additional_root_gb}GB root`}
                      </p>
                      {s.notes && (
                        <p className="text-xs font-semibold" style={{ color: 'hsl(var(--c4p-brand-blue))' }}>
                          Purpose: {s.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              } />
          )}

          {quote.extras?.length > 0 && (
            <SectionCard icon={Package} iconBg="#fefce8" iconColor="#b45309" title="Extras"
              editStep={6} quoteId={id} isOwner={canEdit}
              value={
                <div className="space-y-1 text-sm">
                  {quote.extras.map((e, i) => <p key={i}>{e.quantity}× {e.extra_name}</p>)}
                </div>
              } />
          )}

          {quote.product_code === 'MTPRO' && quote.mtpro_tenants?.length > 0 && (() => {
            const STATUS_COLORS = { new: '#16a34a', existing: '#0ea5e9', adding_users: '#d97706' };
            const STATUS_LABELS = { new: 'New', existing: 'Existing', adding_users: 'Adding Users' };
            const totalAllocated = quote.mtpro_tenants.reduce((s, t) => s + (t.num_users || 0), 0);
            return (
              <div className="rounded-xl border bg-white p-5" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: '#ecfeff' }}>
                    <Users size={15} style={{ color: '#0891b2' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>MTPro Tenant Breakdown</p>
                        <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                          {quote.mtpro_tenants.length} tenant{quote.mtpro_tenants.length !== 1 ? 's' : ''} · {totalAllocated} of {quote.num_users} users allocated
                        </p>
                      </div>
                      {canEdit && (
                        <Link to={`/quotes/${id}/edit?step=8`}>
                          <Button variant="ghost" size="sm" className="gap-1 text-xs flex-shrink-0 -mt-1 -mr-2"><Pencil size={11} /> Edit</Button>
                        </Link>
                      )}
                    </div>
                    <div className="space-y-2">
                      {quote.mtpro_tenants.map((t, i) => (
                    <div key={t.id || i} className="rounded-lg border px-3 py-2.5 text-sm bg-white">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold" style={{ color: NAVY }}>{t.tenant_name}</span>
                        <span className="text-xs px-1.5 py-0.5 rounded-full font-medium" style={{ background: (STATUS_COLORS[t.status] || '#0ea5e9') + '1a', color: STATUS_COLORS[t.status] || '#0ea5e9' }}>
                          {STATUS_LABELS[t.status] || t.status}
                        </span>
                        <span className="font-bold tabular-nums text-xs" style={{ color: 'hsl(var(--c4p-orange-600))' }}>{t.num_users} users</span>
                      </div>
                      {t.assigned_servers?.length > 0 && (
                        <p className="text-xs mt-1" style={{ color: 'hsl(var(--muted-fg))' }}>{t.assigned_servers.join(' · ')}</p>
                      )}
                      {t.notes && <p className="text-xs mt-0.5 italic" style={{ color: 'hsl(var(--muted-fg))' }}>{t.notes}</p>}
                    </div>
                  ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Right: pricing summary */}
        <div className="space-y-4">
          {snap ? (
            <div className="rounded-xl border bg-white overflow-hidden"
                 style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
                 data-testid="quote-summary-panel">

              {/* Monthly total header */}
              <div className="px-4 py-4"
                   style={{ background: 'linear-gradient(150deg, hsl(218 52% 11%) 0%, hsl(210 48% 17%) 100%)' }}>
                <p className="text-xs font-bold uppercase tracking-widest mb-2"
                   style={{ color: 'hsl(var(--c4p-brand-blue))' }}>Monthly Total</p>
                <div className="text-[2rem] font-bold tabular-nums leading-none"
                     style={{ fontFamily: 'var(--font-display)', color: 'white' }}
                     data-testid="quote-detail-monthly-total">
                  {sym(currency)}{fmtN(displayTotal)}
                </div>
                {currency === 'GBP' && displayTotal > 0 && (
                  <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {sym(currency)}{fmtN(incVat)} per month including VAT
                  </p>
                )}
              </div>

              {/* Customer context bar */}
              <div className="px-4 py-2.5"
                   style={{ background: 'hsl(var(--muted) / 0.5)', borderBottom: '1px solid hsl(var(--border))' }}>
                <p className="text-xs font-semibold">{quote.customer_name}</p>
                <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                  {productLabel}{(!isCustomQuote || hasDbServer) ? ` · ${isCustomQuote ? customDbTypesDisplay(dbTypeLabelShort) : (quote.product_code !== 'STANDARD' ? dbTypeLabelShort(quote.db_server?.db_type || 'HANA') : dbTypeLabelShort(quote.db_type))}` : ''}{!isCustomZeroUsers ? ` · ${quote.num_users} users` : ''} · {quote.term_months} months · {currency}
                  {quote.region_name && ` · ${quote.region_name}`}
                  {quote.billing_model && quote.billing_model !== 'PARTNER' && ` · ${BILLING_LABELS[quote.billing_model]}`}
                </p>
                {isTenantDirect && quote.end_customer_name && (
                  <p className="text-xs mt-0.5" style={{ color: 'hsl(265 89% 45%)' }}>Tenant: {quote.end_customer_name}</p>
                )}
              </div>

              {/* Line items table */}
              {billable.length > 0 && (
                <div className="overflow-x-auto -mx-1">
                <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
                      <th className="text-right font-semibold px-3 py-2" style={{ color: 'hsl(var(--muted-fg))', width: '36px' }}>Qty</th>
                      <th className="text-left font-semibold px-2 py-2" style={{ color: 'hsl(var(--muted-fg))' }}>Description</th>
                      <th className="text-right font-semibold px-2 py-2" style={{ color: 'hsl(var(--muted-fg))', whiteSpace: 'nowrap' }}>Unit Price</th>
                      <th className="text-right font-semibold px-3 py-2" style={{ color: 'hsl(var(--muted-fg))', whiteSpace: 'nowrap' }}>Monthly</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isCustomZeroUsers
                      ? allSnapLines.filter(l => l.category !== 'base' && l.category !== 'inclusion')
                      : allSnapLines
                    ).map((l, i) => {
                      // Tenant Direct: show the saved bespoke tenant price if set, otherwise
                      // gross up PUPM and non-user lines via the standard margin formula.
                      const hasBespoke = isTenantDirect && l.tenant_unit_price != null;
                      const tdBase = isTenantDirect && !hasBespoke && l.category === 'base';
                      const tdNonUser = isTenantDirect && !hasBespoke && !tdBase && !l.is_included;
                      const tenantPupm = tdBase ? (l.unit_price || 0) + (snap.partner_margin_pupm || 0) : null;
                      const nonUserFactor = tdNonUser ? 1 + ((snap.partner_margin_percent || 0) / 100) : 1;

                      const displayUnitPrice = hasBespoke ? l.tenant_unit_price
                        : tenantPupm !== null ? tenantPupm
                        : tdNonUser && l.unit_price > 0 ? Math.round(l.unit_price * nonUserFactor * 100) / 100
                        : l.unit_price;
                      const displayMonthly = hasBespoke ? (l.tenant_monthly_amount ?? l.tenant_unit_price * (l.qty || 0))
                        : tenantPupm !== null ? tenantPupm * (snap.num_users || 0)
                        : l.monthly_amount > 0 ? Math.round(l.monthly_amount * nonUserFactor * 100) / 100
                        : l.monthly_amount;
                      const displayOneoff = l.oneoff_amount > 0 ? Math.round(l.oneoff_amount * nonUserFactor * 100) / 100 : 0;

                      const qty = l.qty != null
                        ? (l.qty === Math.floor(l.qty) ? Math.floor(l.qty) : l.qty)
                        : '-';
                      const qtyDisplay = qty === '-' ? '-' : (
                        <>{qty}{l.qty_unit && <><br/><span style={{ fontSize: '10px', color: 'hsl(var(--muted-fg))' }}>{l.qty_unit}</span></>}</>
                      );
                      const unitPrice = !l.is_included && displayUnitPrice != null && displayUnitPrice > 0
                        ? `${sym(currency)}${fmtN(displayUnitPrice)}`
                        : '';
                      const monthly = l.is_included ? 'Included'
                        : displayMonthly > 0 ? `${sym(currency)}${fmtN(displayMonthly)}`
                        : displayOneoff > 0 ? `${sym(currency)}${fmtN(displayOneoff)}`
                        : '-';
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid hsl(var(--border))', opacity: l.is_included ? 0.65 : 1 }}>
                          <td className="text-right tabular-nums px-3 py-2 font-medium"
                              style={{ color: 'hsl(var(--muted-fg))', verticalAlign: 'top' }}>{qty}</td>
                          <td className="px-2 py-2" style={{ verticalAlign: 'top' }}>
                            <p className={`font-semibold${l.is_included ? ' text-xs' : ''}`}
                               style={{ color: l.is_included ? 'hsl(var(--muted-fg))' : NAVY }}>{l.label}</p>
                            {l.sub_label && <p className="mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>{l.sub_label}</p>}
                            {l.item_code && <p className="mt-0.5 font-mono" style={{ color: 'hsl(var(--muted-fg))', fontSize: '10px', opacity: 0.7 }}>{l.item_code}</p>}
                          </td>
                          <td className="text-right tabular-nums px-2 py-2 text-xs"
                              style={{ color: 'hsl(var(--muted-fg))', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{unitPrice}</td>
                          <td className="text-right tabular-nums font-bold px-3 py-2"
                              style={{ color: l.is_included ? 'hsl(var(--muted-fg))' : NAVY, verticalAlign: 'top', whiteSpace: 'nowrap',
                                       fontSize: l.is_included ? '11px' : '12px' }}>{monthly}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}

              {/* VAT section - GBP only */}
              {currency === 'GBP' && displayTotal > 0 && (
                <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: '11px', borderTop: '2px solid hsl(var(--border))' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                      <td className="px-3 py-1.5" style={{ width: '36px' }} />
                      <td className="px-2 py-1.5" style={{ color: '#d97706' }}>VAT @ 20%</td>
                      <td className="px-2 py-1.5" />
                      <td className="text-right tabular-nums px-3 py-1.5"
                          style={{ color: '#d97706', whiteSpace: 'nowrap' }}>{sym(currency)}{fmtN(vat)}</td>
                    </tr>
                    <tr style={{ background: 'hsl(218 52% 11%)', color: 'white' }}>
                      <td className="px-3 py-2" />
                      <td className="px-2 py-2 font-bold text-xs">Monthly inc. VAT</td>
                      <td className="px-2 py-2" />
                      <td className="text-right tabular-nums font-bold px-3 py-2"
                          style={{ whiteSpace: 'nowrap' }}>{sym(currency)}{fmtN(incVat)}</td>
                    </tr>
                  </tbody>
                </table>
              )}

              {/* Snapshot footer */}
              <div className="px-4 py-2.5 text-xs"
                   style={{ borderTop: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))', color: 'hsl(var(--muted-fg))' }}>
                Snapshot: {new Date(snap.snapshot_at).toLocaleDateString('en-GB')}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border bg-white p-5 text-center">
              <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>No saved pricing snapshot. Edit and save the quote to generate pricing.</p>
            </div>
          )}
        </div>
      </div>
    </div>

      {/* ── Dialogs ── */}
      {/* Variant Comparison */}
      {showCompare && variants.length >= 2 && (
        <VariantCompareModal variants={variants} currentId={id} onClose={() => setShowCompare(false)} />
      )}

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 size={16} style={{ color: 'hsl(var(--danger))' }} /> Delete Quote
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            Are you sure you want to delete <strong>{quote?.quote_number}</strong>? This action cannot be undone.
          </p>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting}
              style={{ background: 'hsl(var(--danger))', color: 'white' }}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete Quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send for Signing Modal */}
      <Dialog open={showSignModal} onOpenChange={v => { setShowSignModal(v); if (!v) setSigningLink(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine size={16} style={{ color: '#16a34a' }} /> Send for Signing
            </DialogTitle>
          </DialogHeader>
          {!signingLink ? (
            <div className="space-y-4">
              <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
                A secure, one-time signing link will be created for <strong>{quote?.quote_number}</strong>.
                The recipient will verify their identity via email OTP before signing.
              </p>

              {/* MSA blocker alert */}
              {msaBlocker && (
                <div className="rounded-lg p-4" data-testid="msa-blocker-alert"
                  style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <div className="flex items-start gap-2">
                    <AlertCircle size={15} style={{ color: '#dc2626', flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: '#991b1b' }}>Cannot send for signing</p>
                      <p className="text-sm mt-0.5" style={{ color: '#7f1d1d' }}>
                        <strong>{msaBlocker.name}</strong>'s MSA has not been recorded as signed.{' '}
                        {msaBlocker.blocker === 'partner'
                          ? 'A Super Admin must record the Partner MSA before this quote can be sent.'
                          : msaBlocker.billing_via_partner === false
                            ? 'This tenant is configured for Direct Billing and requires its own MSA. A Super Admin must record the Tenant MSA.'
                            : 'This quote uses Tenant Direct billing. A Super Admin must record the Tenant MSA before this quote can be sent.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick-select from wizard contacts — hidden when MSA blocks signing */}
              {!msaBlocker && (() => {
                const isTenantDirect = quote?.billing_model === 'TENANT_DIRECT';
                const tenantContacts = quote?.tenant_contacts || [];
                const contactIds     = quote?.contact_ids || [];
                const allContacts    = quote?.customer_contacts || [];

                // For Tenant Direct: show tenant contacts in picker (not partner contacts)
                const contacts = isTenantDirect ? tenantContacts : allContacts;
                // Determine which were "wizard-selected"
                const wizardPicked = isTenantDirect
                  ? tenantContacts   // all tenant contacts are "wizard-selected" for TD billing
                  : allContacts.filter(c => contactIds.includes(c.id) && c.email);
                const rest         = isTenantDirect
                  ? []
                  : allContacts.filter(c => !contactIds.includes(c.id) && c.email && c.is_active !== false);
                const pills = [...wizardPicked, ...rest].slice(0, 5);
                if (!pills.length) return null;
                return (
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
                      Select a contact {isTenantDirect && <span style={{ color: '#7c3aed' }}>· Tenant contacts</span>}
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {pills.map(c => {
                        const active = signEmail === c.email;
                        return (
                          <button
                            key={c.id || c.email}
                            type="button"
                            onClick={() => { setSignEmail(c.email); setSignName(c.name || ''); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all text-left"
                            style={active
                              ? { background: '#f0fdf4', borderColor: '#16a34a', color: '#14532d' }
                              : { background: 'hsl(var(--muted))', borderColor: 'hsl(var(--border))', color: 'hsl(var(--fg))' }}
                          >
                            <span className="font-semibold">{c.name}</span>
                            <span style={{ opacity: 0.6 }}>· {c.email}</span>
                            {c._isTenant && (
                              <span className="text-xs px-1 rounded" style={{ background: '#ede9fe', color: '#5b21b6' }}>
                                tenant
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {!msaBlocker && (
                <div className="space-y-3" style={{ borderTop: '1px solid hsl(var(--border))', paddingTop: '12px' }}>
                  <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-fg))' }}>
                    Or type manually
                  </label>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium" style={{ color: NAVY }}>Email address *</label>
                    <Input type="email" value={signEmail} onChange={e => setSignEmail(e.target.value)}
                      placeholder="partner@company.com" data-testid="sign-email-input" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium" style={{ color: NAVY }}>Recipient name</label>
                    <Input value={signName} onChange={e => setSignName(e.target.value)}
                      placeholder="e.g. John Smith" data-testid="sign-name-input" />
                  </div>
                </div>
              )}

              {!msaBlocker && (
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'hsl(var(--muted))' }}>
                  <input type="checkbox" id="sendEmailCb" checked={sendEmail && signingEmailAllowed}
                    onChange={e => signingEmailAllowed && setSendEmail(e.target.checked)}
                    disabled={!signingEmailAllowed}
                    className="w-4 h-4" style={{ accentColor: NAVY, cursor: signingEmailAllowed ? 'pointer' : 'not-allowed', opacity: signingEmailAllowed ? 1 : 0.4 }} />
                  <label htmlFor="sendEmailCb" className="text-sm" style={{ cursor: signingEmailAllowed ? 'pointer' : 'default', opacity: signingEmailAllowed ? 1 : 0.5 }}>
                    Also send the signing link by email automatically
                    {!signingEmailAllowed && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded font-medium" style={{ background: '#fef9c3', color: '#92400e' }}>disabled by admin</span>}
                  </label>
                </div>
              )}

              {/* Footer: Close only when MSA blocks; Cancel + Create when clear */}
              {msaBlocker ? (
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSignModal(false)}>Close</Button>
                </DialogFooter>
              ) : (
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSignModal(false)}>Cancel</Button>
                  <Button onClick={handleCreateSigningLink} disabled={signingBusy || !signEmail}
                    style={{ background: '#16a34a', color: 'white' }} className="gap-1.5">
                    {signingBusy ? <Loader2 size={14} className="animate-spin" /> : <PenLine size={14} />}
                    Create Signing Link
                  </Button>
                </DialogFooter>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg p-4 text-center" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <CheckCircle2 size={28} className="mx-auto mb-2" style={{ color: '#16a34a' }} />
                <p className="font-semibold text-sm" style={{ color: '#14532d' }}>Signing link created!</p>
                {sendEmail && <p className="text-xs mt-1" style={{ color: '#16a34a' }}>Email sent to {signEmail}</p>}
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide block mb-1.5" style={{ color: 'hsl(var(--muted-fg))' }}>
                  Signing link - valid for 30 days
                </label>
                <div className="flex gap-2">
                  <Input value={signingLink} readOnly className="text-xs font-mono" />
                  <Button onClick={copyLink} variant="outline" size="sm" className="gap-1 flex-shrink-0">
                    <Copy size={13} /> Copy
                  </Button>
                </div>
              </div>
              <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                Share this link with the partner. They will need to verify their identity via a one-time code sent to their email before signing.
              </p>
              <DialogFooter>
                <Button onClick={() => { setShowSignModal(false); setSigningLink(''); }}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Summary Dialog */}
      <Dialog open={summaryDialog} onOpenChange={setSummaryDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Quote Summary</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap"
              style={{ background: 'hsl(var(--muted))' }}>{summaryText}</div>
            <Button variant="outline" className="w-full gap-2"
              onClick={() => { navigator.clipboard.writeText(summaryText); toast.success('Copied'); }}
              data-testid="copy-summary-button">
              <ClipboardCopy size={13} /> Copy to Clipboard
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert Manual Quote to Contract Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={16} style={{ color: '#ea580c' }} /> Convert to Contract
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg p-3" style={{ background: '#fff7ed', border: '1px solid #fdba74' }}>
              <p className="text-xs" style={{ color: '#9a3412' }}>
                This will create a <strong>live contract</strong> from <strong>{quote?.quote_number}</strong> immediately — no signing required. The contract will be flagged as <strong>Manual</strong>.
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" style={{ color: NAVY }}>Contract Start Date</label>
              <Input
                type="date"
                value={convertStartDate}
                onChange={e => setConvertStartDate(e.target.value)}
                data-testid="convert-contract-start-date"
              />
              <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                Can be set in the past for legacy contracts. Leave blank to use today's date.
              </p>
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>Cancel</Button>
            <Button onClick={handleConvertToContract} disabled={converting}
              style={{ background: '#ea580c', color: 'white' }}
              data-testid="convert-contract-confirm-btn">
              {converting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Building2 size={14} className="mr-2" />}
              Create Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
